import { useStore } from '@tanstack/react-store';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { type Choice } from '../../components/SearchableMultiSelect';
import { TRPCContext } from '../../contexts/TRPC';
import type { AuthState } from '../../hooks/useAuth';
import { downloadFile } from '../../lib/formatters';
import type { AuditDetail } from '../../stores/auditManagementStore';
import {
  auditManagementStore,
  setAudits,
  setLoading,
  setPaginationInfo,
} from '../../stores/auditManagementStore';
import { canAccessAdminPages } from '../../utils/roleUtils';

export const useAuditData = (authState: AuthState) => {
  const trpc = use(TRPCContext);
  const [actionOptions, setActionOptions] = useState<Choice[]>([]);
  const [entityTypeOptions, setEntityTypeOptions] = useState<Choice[]>([]);

  const filters = useStore(auditManagementStore, (state) => state.filters);
  const sorting = useStore(auditManagementStore, (state) => state.sorting);
  const pagination = useStore(
    auditManagementStore,
    (state) => state.pagination,
  );
  const isLoading = useStore(
    auditManagementStore,
    (state) => state.ui.isLoading,
  );
  const audits = useStore(auditManagementStore, (state) => state.audits);

  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!trpc?.client) return;
      if (!canAccessAdminPages(authState)) return;

      try {
        const result = await trpc.client.getAuditFilterOptions.query({});
        if (isRight(result)) {
          setActionOptions(
            result.right.actions.map((action) => ({
              key: action,
              label: action,
              value: action,
            })),
          );
          setEntityTypeOptions(
            result.right.entityTypes.map((type) => ({
              key: type,
              label: type,
              value: type,
            })),
          );
        }
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };

    loadFilterOptions();
  }, [trpc, authState]);

  const loadAudits = async () => {
    if (!trpc?.client) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const apiSorting =
        sorting.length > 0
          ? sorting
              .filter((sort) =>
                ['timestamp', 'createdAt', 'status', 'duration'].includes(
                  sort.id,
                ),
              )
              .map((sort) => ({
                id: sort.id as
                  | 'timestamp'
                  | 'createdAt'
                  | 'status'
                  | 'duration',
                desc: sort.desc,
              }))
          : undefined;

      const result = await trpc.client.listAudits.query({
        filters: {
          status: filters.status,
          actions: filters.actions.length > 0 ? filters.actions : undefined,
          entityTypes:
            filters.entityTypes.length > 0 ? filters.entityTypes : undefined,
          entityId: filters.entityId || undefined,
          userId: filters.userId || undefined,
          search: filters.search || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        },
        pagination: {
          page: pagination.currentPage,
          limit: 50,
        },
        sorting: apiSorting,
      });

      if (isRight(result)) {
        const auditsWithChanges = result.right.audits.map((audit) => ({
          ...audit,
          changes: audit.changes || {},
          status: audit.status,
        })) as AuditDetail[];

        setAudits(auditsWithChanges);
        setPaginationInfo(
          pagination.currentPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    if (!trpc?.client) return;

    try {
      const result = await trpc.client.exportAudits.query({
        filters: {
          status: filters.status,
          actions: filters.actions.length > 0 ? filters.actions : undefined,
          entityTypes:
            filters.entityTypes.length > 0 ? filters.entityTypes : undefined,
          entityId: filters.entityId || undefined,
          userId: filters.userId || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        },
        format,
      });

      if (isRight(result)) {
        downloadFile(
          result.right.data,
          result.right.filename,
          result.right.mimeType,
        );
        toast.success(`Exported ${pagination.total} audit records`);
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to export audit logs');
    }
  };

  useEffect(() => {
    if (canAccessAdminPages(authState)) {
      loadAudits();
    }
  }, [authState, filters, pagination.currentPage, sorting]);

  return {
    audits,
    isLoading,
    filters,
    sorting,
    pagination,
    actionOptions,
    entityTypeOptions,
    loadAudits,
    handleExport,
  };
};
